<script setup lang="ts">
import type { Component } from 'vue';
import { BookOpen, GitBranch, Github, Gitlab, Globe, LifeBuoy, Link, Mail, MessageCircle, Send, Twitter, Youtube } from 'lucide-vue-next';
import type { PublisherLink, PublisherLinkKind } from '@vp/shared-types';
import { locale, t } from '../i18n';
import { safeUrl } from '../editor/bookmarks';

/** API 2.4: the publisher's links of a module (manifest `links`) as small pills; they open in a new tab. */
const props = defineProps<{ links: PublisherLink[] }>();

const ICONS: Record<PublisherLinkKind, Component> = {
  website: Globe,
  repository: GitBranch,
  docs: BookOpen,
  support: LifeBuoy,
  email: Mail,
  github: Github,
  gitlab: Gitlab,
  telegram: Send,
  vk: MessageCircle,
  x: Twitter,
  youtube: Youtube,
  discord: MessageCircle,
  other: Link,
};

function label(l: PublisherLink): string {
  if (l.label) return typeof l.label === 'string' ? l.label : (l.label[locale.value] ?? l.label['en'] ?? Object.values(l.label)[0] ?? '');
  if (l.kind === 'email') return l.url.replace(/^mailto:/, '');
  return t('modules.linkKind.' + l.kind);
}
function title(l: PublisherLink): string {
  return l.kind === 'email' ? l.url.replace(/^mailto:/, '') : l.url.replace(/^https:\/\//, '');
}
// the manifest is checked already; the app opens nothing but http(s) and mailto in any case
const shown = () => props.links.filter((l) => !!safeUrl(l.url));
</script>

<template>
  <div v-if="shown().length" class="pub-links">
    <a v-for="l in shown()" :key="l.url" class="pub-link" :href="safeUrl(l.url)!" target="_blank" rel="noopener noreferrer" :title="title(l)" :data-link-kind="l.kind">
      <component :is="ICONS[l.kind]" :size="14" />
      <span>{{ label(l) }}</span>
    </a>
  </div>
</template>

<style scoped>
.pub-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pub-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 11px;
  border-radius: 999px;
  border: 1px solid var(--border);
  color: var(--text);
  font-size: 12.5px;
  text-decoration: none;
  background: var(--panel);
}
.pub-link:hover {
  border-color: var(--accent);
  color: var(--accent);
}
</style>
